import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  ExternalLink, 
  CheckCircle, 
  Calendar, 
  MapPin, 
  Building2, 
  Clock,
  Award,
  Shield,
  ArrowLeft
} from "lucide-react";

const TokenViewer = () => {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTokenData();
  }, [tokenId]);

  const fetchTokenData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blockchain/token/${tokenId}`);
      setToken(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Token not found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-amber-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading token data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-amber-50 to-red-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Token Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate("/")} className="!bg-red-600 hover:!bg-red-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const polygonscanUrl = `https://amoy.polygonscan.com/token/${token.contract_address}?a=${token.token_id}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-amber-50 to-red-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-600 to-amber-600 rounded-full mb-4">
            <Award className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            PlayBack Token #{token.token_id}
          </h1>
          <p className="text-gray-600">Verified Volunteer Hour Token</p>
        </div>

        {/* Verification Badge */}
        <Card className="mb-6 border-2 border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-green-900">Blockchain Verified</h3>
                <p className="text-sm text-green-700">
                  This token is permanently recorded on Polygon blockchain and cannot be altered or deleted.
                </p>
              </div>
              <Button
                onClick={() => window.open(polygonscanUrl, '_blank')}
                className="!bg-green-600 hover:!bg-green-700 !text-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Verify on Polygonscan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Token Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Volunteer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                Volunteer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Volunteer ID</p>
                <p className="font-mono text-lg font-semibold text-gray-900">
                  {token.volunteer_id.slice(0, 8)}...{token.volunteer_id.slice(-6)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Hours Contributed</p>
                <p className="text-2xl font-bold text-red-600">{token.hours} hours</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Activity Type</p>
                <Badge variant="outline" className="text-sm capitalize">
                  {token.activity_type}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Club Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-600" />
                Organization
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Club Name</p>
                <p className="font-semibold text-gray-900">{token.club_name}</p>
              </div>
              {token.club_type && (
                <div>
                  <p className="text-sm text-gray-600">Club Type</p>
                  <Badge className="!bg-amber-100 !text-amber-900 capitalize">
                    {token.club_type}
                  </Badge>
                </div>
              )}
              {(token.club_country || token.club_county) && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Location
                  </p>
                  <p className="text-gray-900">
                    {[token.club_county, token.club_country].filter(Boolean).join(", ")}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Date & Time */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Date of Service
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Service Date</p>
                <p className="font-semibold text-gray-900">
                  {new Date(token.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Minted On Blockchain
                </p>
                <p className="text-sm text-gray-900">
                  {new Date(token.minted_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Blockchain Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Blockchain Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Transaction Hash</p>
                <p className="font-mono text-xs text-gray-900 break-all">
                  {token.transaction_hash.slice(0, 10)}...{token.transaction_hash.slice(-8)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Network</p>
                <Badge variant="outline">Polygon Amoy Testnet</Badge>
              </div>
              {token.gas_cost && (
                <div>
                  <p className="text-sm text-gray-600">Gas Cost</p>
                  <p className="text-sm text-gray-900">{token.gas_cost} MATIC</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Purchased By (if applicable) */}
        {token.purchasing_company && (
          <Card className="mb-6 border-2 border-purple-200">
            <CardHeader>
              <CardTitle className="text-purple-900">ESG Impact</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                This volunteer hour token has been purchased by <span className="font-semibold">{token.purchasing_company}</span> to support their Environmental, Social, and Governance (ESG) reporting and community impact initiatives.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Bottom Actions */}
        <div className="flex gap-4 justify-center">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="border-gray-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <Button
            onClick={() => window.open(polygonscanUrl, '_blank')}
            className="!bg-red-600 hover:!bg-red-700"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View on Polygonscan
          </Button>
        </div>

        {/* Trust Badge */}
        <div className="text-center mt-8 text-sm text-gray-600">
          <p>
            This token is part of the PlayBack Token ecosystem, connecting volunteers, 
            community organizations, and companies through verified, blockchain-recorded social impact.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TokenViewer;
